import { format, parseISO } from 'date-fns'
import { CommentComment, CommentsDocument } from '../graphql/generated'

export interface Comment {
  id: string
  name: string
  date: string
  body: string
  parent: string | null
  children?: Comment[]
}

const createTree = (comments: Comment[]) => {
  // A lookup object to reference comments by their IDs
  let commentMap: Record<string, Comment> = {}

  // First pass: Create a mapping of comments by their IDs
  comments.forEach((comment) => {
    comment.children = [] // Initialize an empty array for the children
    commentMap[comment.id] = comment
  })

  // Second pass: Assign comments to their parent's children array or to a top-level array
  let nestedComments: Comment[] = []
  comments.forEach((comment) => {
    if (comment.parent) {
      if (commentMap[comment.parent]) {
        commentMap[comment.parent]?.children?.push(comment)
      } else {
        // In case there's a reference to a parent that doesn't exist in the list
        nestedComments.push(comment)
      }
    } else {
      nestedComments.push(comment)
    }
  })

  return nestedComments
}

const transformResponse = (data: CommentComment[]): Comment[] => {
  const comments =
    data?.filter(Boolean)?.map((item) => {
      return {
        id: item.entityId || '',
        name: item.fieldAuthorName || '',
        date: format(parseISO(item.entityCreated || ''), 'dd.MM.yyyy'),
        body: item.commentBody?.processed || '',
        parent: item.pid?.targetId?.toString() || null,
      }
    }) || []
  return createTree(comments)
}

const getItems = async (
  document: any,
  query: any,
  variables: any,
): Promise<{
  items: Comment[]
  total: number
}> => {
  const { data } = await query({
    query: document,
    variables,
  })

  return {
    items: transformResponse(data.commentQuery?.entities as CommentComment[]),
    total: data.commentQuery?.count || 0,
  }
}
export const useComments = async (entityId: string) => {
  const { clients } = useApollo()

  const getVariables = () => ({
    entityId,
  })

  // Initial data fetch with useAsyncData for SSR
  const { data: initialData } = await useAsyncData('comments', async () => {
    return await getItems(CommentsDocument, clients!.default.query, getVariables())
  })

  const data = ref(initialData.value ?? { items: [], total: 0 })
  
  const refetch = async () => {
    // Use direct call for reactive updates
    data.value = await getItems(CommentsDocument, clients!.default.query, getVariables())
  }

  const items = computed(() => data.value.items)

  return { items, refetch } as const
}
